import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, OnChanges, signal, inject, ChangeDetectionStrategy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

interface LinkPreviewData {
    title?: string;
    description?: string;
    image?: string;
    url: string;
    site_name?: string;
}

// Cache estático para evitar llamadas duplicadas
const previewCache = new Map<string, LinkPreviewData | null>();
const pendingRequests = new Map<string, Promise<LinkPreviewData | null>>();

@Component({
    selector: 'link-preview',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './link-preview.component.html',
    styleUrls: ['./link-preview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LinkPreviewComponent implements OnInit, OnChanges {
    private http = inject(HttpClient);

    @Input({ required: true }) url!: string;
    @Input() colorVariant: number = 0; // 0-3 para variaciones sutiles de azul

    preview = signal<LinkPreviewData | null>(null);
    loading = signal(true);
    error = signal(false);
    imageError = signal(false);

    private hasLoaded = false;

    ngOnInit() {
        this.loadPreviewIfNeeded();
    }

    ngOnChanges() {
        // Si la URL cambia, recargar
        if (this.hasLoaded && this.url) {
            this.loadPreviewIfNeeded();
        }
    }

    private loadPreviewIfNeeded() {
        if (!this.url || this.url.length < 10) {
            return; // URL muy corta, esperar
        }

        // Si ya está en cache, usar directamente
        if (previewCache.has(this.url)) {
            const cached = previewCache.get(this.url);
            if (cached) {
                this.preview.set(cached);
            } else {
                this.error.set(true);
            }
            this.loading.set(false);
            this.hasLoaded = true;
            return;
        }

        // Si ya hay una petición pendiente para esta URL, esperar
        if (pendingRequests.has(this.url)) {
            pendingRequests.get(this.url)!.then(data => {
                if (data) {
                    this.preview.set(data);
                } else {
                    this.error.set(true);
                }
                this.loading.set(false);
            });
            return;
        }

        this.fetchPreview();
    }

    private async fetchPreview() {
        const url = this.url;

        const requestPromise = (async (): Promise<LinkPreviewData | null> => {
            try {
                const apiUrl = `${environment.apiAssistantUrlBase}/api/link-preview?url=${encodeURIComponent(url)}`;
                const data = await this.http.get<LinkPreviewData>(apiUrl).toPromise();

                if (data && (data.title || data.image)) {
                    // Convertir URLs de imagen relativas a absolutas (para imágenes generadas por Gemini)
                    if (data.image && data.image.startsWith('/api/')) {
                        data.image = `${environment.apiAssistantUrlBase}${data.image}`;
                    }
                    return data;
                }
                return null;
            } catch (e) {
                console.warn('[LinkPreview] Error fetching preview:', e);
                return null;
            }
        })();

        pendingRequests.set(url, requestPromise);

        try {
            const result = await requestPromise;
            previewCache.set(url, result);

            if (result) {
                this.preview.set(result);
            } else {
                this.error.set(true);
            }
        } finally {
            pendingRequests.delete(url);
            this.loading.set(false);
            this.hasLoaded = true;
        }
    }

    onImageError() {
        this.imageError.set(true);
    }

    openLink() {
        window.open(this.url, '_blank', 'noopener,noreferrer');
    }

    /** Devuelve la URL de forma legible (solo dominio) */
    getDisplayUrl(): string {
        try {
            const parsed = new URL(this.url);
            return parsed.hostname.replace('www.', '');
        } catch {
            return this.url;
        }
    }
}
